import React, { useState, useEffect } from "react";
import "./VisualFirstDashboard.scss";

/**
 * VISUAL FIRST DASHBOARD - Where imagery drives everything
 * Rich visuals, game art, brand immersion - stats support the story
 */
const VisualFirstDashboard: React.FC = () => {
    const [activeGame, setActiveGame] = useState("valorant");
    const [selectedQuest, setSelectedQuest] = useState(0);

    // Mock data with real visual focus
    const gameData = {
        valorant: {
            hero: "https://images.contentstack.io/v3/assets/bltb6530b271fddd0b1/blt8a6e2fec6c9e7223/64f1e4d789e2e84e8a21ae4e/EP7_Jett_1920x1080.jpg",
            character: "https://images.contentstack.io/v3/assets/bltb6530b271fddd0b1/blt578f5b30c89da32f/5eb7cdc19df5cf37047009d1/V_AGENTS_587x900_Jett.png",
            logo: "https://upload.wikimedia.org/wikipedia/commons/f/fc/Valorant_logo_-_pink_color_version.svg",
            color: "#FF4655",
            stats: {
                rank: "Radiant",
                rankIcon: "https://trackercdn.com/cdn/tracker.gg/valorant/icons/tiersv2/24.png",
                kd: 1.84,
                winRate: 68.4,
                headshot: 28.5,
            },
        },
        fortnite: {
            hero: "https://cdn2.unrealengine.com/blade-2910x1170-2910x1170-e1f31f86b297.jpg",
            character: "https://static.wikia.nocookie.net/fortnite/images/9/95/Midas_-_Outfit_-_Fortnite.png",
            logo: "https://upload.wikimedia.org/wikipedia/commons/7/7c/Fortnite_F_lettermark_logo.png",
            color: "#9D4DFF",
            stats: {
                rank: "Champion",
                rankIcon: "https://progameguides.com/wp-content/uploads/2022/01/fortnite-rank-champion-league.png",
                kd: 3.21,
                winRate: 15.2,
                wins: 847,
            },
        },
        apex: {
            hero: "https://media.contentapi.ea.com/content/dam/apex-legends/images/2019/01/apex-featured-image-16x9.jpg.adapt.crop16x9.1023w.jpg",
            character: "https://static.wikia.nocookie.net/apexlegends_gamepedia_en/images/5/5e/Wraith.png",
            logo: "https://upload.wikimedia.org/wikipedia/commons/d/db/Apex_Legends_Logo.svg",
            color: "#DA292A",
            stats: {
                rank: "Predator",
                rankIcon: "https://trackercdn.com/cdn/apex.tracker.gg/ranks/apex_predator.png",
                kd: 2.75,
                winRate: 12.8,
                damage: "1.2M",
            },
        },
    };

    const brandQuests = [
        {
            id: 1,
            brand: "Snapple",
            title: "Fantastic Refresh Challenge",
            image: "https://www.snapple.com/images/products/diet-snapple-peach-tea.png",
            hero: "https://tervis.scene7.com/is/image/tervis/ss-marvel-fantastic-four-group-20oz?$PDPFULL$",
            progress: 75,
            reward: "Exclusive Fantastic 4 Skin",
            xp: 2500,
            timeLeft: "2 days",
            color: "#FF6B35",
        },
        {
            id: 2,
            brand: "Monster Energy",
            title: "Clutch Kings Tournament",
            image: "https://www.monsterenergy.com/media/uploads_image/2020/04/20/1600/800/5de091a19e0b6925e8e8eb3b2701fb09.png",
            hero: "https://cdn.redbull.com/images/c_limit,w_1500,h_1000,f_auto,q_auto/redbullcom/2021/4/28/bjoyoujzjyiobzuu2bap/valorant-champions-tour-stage-2",
            progress: 45,
            reward: "$10,000 Prize Pool",
            xp: 5000,
            timeLeft: "5 hours",
            color: "#95D600",
        },
        {
            id: 3,
            brand: "Razer",
            title: "RGB Rampage",
            image: "https://assets2.razerzone.com/images/pnx.assets/618c0b65424070a1017a7168ea1b6337/razer-wallpaper-logo.jpg",
            hero: "https://storage.googleapis.com/stateless-www-razerbrandportal-c/2023/06/f7aea802-key-visual-without-text.jpg",
            progress: 90,
            reward: "Razer DeathAdder V3",
            xp: 3000,
            timeLeft: "12 hours",
            color: "#00FF00",
        },
    ];

    const currentGame = gameData[activeGame];
    const currentQuest = brandQuests[selectedQuest];

    return (
        <div className="visual-first-dashboard">
            {/* DYNAMIC BACKGROUND */}
            <div className="dynamic-background">
                <img src={currentGame.hero} alt="Background" className="bg-image" />
                <div className="bg-overlay" />
                <div className="bg-gradient" style={{
                    background: `radial-gradient(circle at 20% 50%, ${currentGame.color}44 0%, transparent 50%)`,
                }} />
            </div>

            {/* SIDEBAR WITH GAME CHARACTER */}
            <aside className="visual-sidebar">
                <div className="sidebar-header">
                    <img src="/logos/devourplay-logo.svg" alt="DevourPlay" className="logo" />
                </div>

                {/* CHARACTER SHOWCASE */}
                <div className="character-showcase">
                    <img src={currentGame.character} alt="Character" className="character-img" />
                    <div className="character-glow" style={{ background: currentGame.color }} />
                </div>

                {/* GAME SELECTOR */}
                <div className="game-selector">
                    {Object.entries(gameData).map(([key, game]) =>
                        <button
                            key={key}
                            className={`game-btn ${activeGame === key ? "active" : ""}`}
                            onClick={() => setActiveGame(key)}
                            style={{ "--game-color": game.color } as any}
                        >
                            <img src={game.logo} alt={key} />
                            <span className="game-name">{key.toUpperCase()}</span>
                        </button>)}
                </div>

                {/* VISUAL STATS */}
                <div className="visual-stats">
                    <div className="rank-display">
                        <img src={currentGame.stats.rankIcon} alt="Rank" className="rank-icon" />
                        <div className="rank-info">
                            <div className="rank-name">{currentGame.stats.rank}</div>
                            <div className="rank-progress">
                                <div className="progress-fill" style={{ width: "75%", background: currentGame.color }} />
                            </div>
                        </div>
                    </div>

                    <div className="stat-cards">
                        <div className="stat-visual">
                            <div className="stat-circle" style={{ "--stat-color": currentGame.color } as any}>
                                <svg viewBox="0 0 100 100">
                                    <circle cx="50" cy="50" r="45" fill="none" stroke="#222" strokeWidth="5" />
                                    <circle
                                        cx="50" cy="50" r="45"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="5"
                                        strokeDasharray={`${currentGame.stats.winRate * 2.83} 283`}
                                        transform="rotate(-90 50 50)"
                                    />
                                </svg>
                                <div className="stat-center">
                                    <div className="stat-value">{currentGame.stats.winRate}%</div>
                                    <div className="stat-label">WIN RATE</div>
                                </div>
                            </div>
                        </div>

                        <div className="stat-visual">
                            <div className="kd-display">
                                <div className="kd-value" style={{ color: currentGame.color }}>{currentGame.stats.kd}</div>
                                <div className="kd-label">K/D RATIO</div>
                                <div className="kd-bar">
                                    <div className="kills-bar" style={{ width: "65%", background: currentGame.color }} />
                                    <div className="deaths-bar" style={{ width: "35%" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <main className="main-area">
                {/* HERO QUEST SHOWCASE */}
                <section className="quest-showcase">
                    <div className="showcase-bg">
                        <img src={currentQuest.hero} alt="Quest" />
                        <div className="showcase-overlay" />
                    </div>

                    <div className="showcase-content">
                        <div className="quest-brand">
                            <img src={currentQuest.image} alt={currentQuest.brand} className="brand-product" />
                        </div>

                        <div className="quest-details">
                            <h1 className="quest-title">{currentQuest.title}</h1>
                            <div className="quest-meta">
                                <div className="time-left" style={{ color: currentQuest.color }}>
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                    </svg>
                                    {currentQuest.timeLeft}
                                </div>
                                <div className="quest-reward">
                                    <span className="reward-label">GRAND PRIZE</span>
                                    <span className="reward-value">{currentQuest.reward}</span>
                                </div>
                            </div>

                            <div className="quest-progress-visual">
                                <div className="progress-track">
                                    <div className="progress-glow" style={{ width: `${currentQuest.progress}%`, background: currentQuest.color }} />
                                    <div className="progress-fill" style={{ width: `${currentQuest.progress}%`, background: currentQuest.color }} />
                                    <div className="progress-marker" style={{ left: `${currentQuest.progress}%` }}>
                                        <span className="marker-value">{currentQuest.progress}%</span>
                                    </div>
                                </div>
                                <div className="progress-milestones">
                                    <div className="milestone reached">
                                        <div className="milestone-dot" />
                                        <span>Start</span>
                                    </div>
                                    <div className="milestone reached">
                                        <div className="milestone-dot" />
                                        <span>25%</span>
                                    </div>
                                    <div className="milestone reached">
                                        <div className="milestone-dot" />
                                        <span>50%</span>
                                    </div>
                                    <div className="milestone">
                                        <div className="milestone-dot" />
                                        <span>75%</span>
                                    </div>
                                    <div className="milestone">
                                        <div className="milestone-dot" />
                                        <span>Complete</span>
                                    </div>
                                </div>
                            </div>

                            <button className="quest-cta" style={{ background: currentQuest.color }}>
                                <span>CONTINUE QUEST</span>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </section>

                {/* QUEST GRID WITH VISUAL CARDS */}
                <section className="quest-grid">
                    {brandQuests.map((quest, index) =>
                        <div
                            key={quest.id}
                            className={`quest-visual-card ${selectedQuest === index ? "active" : ""}`}
                            onClick={() => setSelectedQuest(index)}
                            style={{ "--quest-color": quest.color } as any}
                        >
                            <div className="card-background">
                                <img src={quest.hero} alt={quest.title} />
                                <div className="card-overlay" />
                            </div>

                            <div className="card-content">
                                <div className="brand-badge">
                                    <img src={quest.image} alt={quest.brand} />
                                </div>

                                <div className="card-info">
                                    <h3 className="card-title">{quest.title}</h3>
                                    <div className="card-progress">
                                        <div className="progress-ring">
                                            <svg viewBox="0 0 100 100">
                                                <circle cx="50" cy="50" r="45" fill="none" stroke="#222" strokeWidth="8" />
                                                <circle
                                                    cx="50" cy="50" r="45"
                                                    fill="none"
                                                    stroke={quest.color}
                                                    strokeWidth="8"
                                                    strokeDasharray={`${quest.progress * 2.83} 283`}
                                                    transform="rotate(-90 50 50)"
                                                />
                                            </svg>
                                            <div className="progress-text">{quest.progress}%</div>
                                        </div>
                                    </div>

                                    <div className="card-rewards">
                                        <div className="xp-reward">
                                            <span className="xp-icon">⚡</span>
                                            <span>{quest.xp.toLocaleString()} XP</span>
                                        </div>
                                        <div className="time-indicator" style={{ color: quest.color }}>
                                            {quest.timeLeft}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>)}
                </section>

                {/* LIVE ACTIVITY FEED WITH VISUALS */}
                <section className="visual-activity">
                    <h2 className="section-title">LIVE ACTIVITY</h2>
                    <div className="activity-stream">
                        <div className="activity-item">
                            <img src="https://via.placeholder.com/48" alt="Player" className="player-thumb" />
                            <div className="activity-content">
                                <div className="activity-text">
                                    <span className="player-name" style={{ color: gameData.valorant.color }}>TenZ</span>
                                    just unlocked the Snapple Phantom Skin!
                                </div>
                                <div className="activity-visual">
                                    <img src="https://valorantinfo.com/images/us/phantom-valorant.webp" alt="Reward" />
                                </div>
                            </div>
                            <div className="activity-time">2m ago</div>
                        </div>

                        <div className="activity-item featured">
                            <img src="https://via.placeholder.com/48" alt="Player" className="player-thumb" />
                            <div className="activity-content">
                                <div className="activity-text">
                                    <span className="player-name" style={{ color: gameData.fortnite.color }}>Ninja</span>
                                    won the Monster Energy Tournament!
                                </div>
                                <div className="activity-reward">
                                    <span className="reward-amount">$5,000</span>
                                    <img src="https://www.monsterenergy.com/media/uploads_image/2020/04/20/1600/800/5de091a19e0b6925e8e8eb3b2701fb09.png" alt="Monster" />
                                </div>
                            </div>
                            <div className="activity-time">15m ago</div>
                        </div>
                    </div>
                </section>
            </main>

            {/* RIGHT PANEL - SQUAD & SOCIAL */}
            <aside className="social-panel">
                <div className="squad-visual">
                    <h3 className="panel-title">YOUR SQUAD</h3>
                    <div className="squad-grid">
                        {["Shadow", "Phoenix", "Ghost", "Reaper"].map((name, i) =>
                            <div key={name} className="squad-mate">
                                <div className="mate-visual">
                                    <img src={`https://via.placeholder.com/80?text=${name}`} alt={name} />
                                    <div className="status-indicator" style={{
                                        background: i < 2 ? "#4CAF50" : "#666",
                                    }} />
                                </div>
                                <div className="mate-name">{name}</div>
                                <div className="mate-game" style={{ color: currentGame.color }}>
                                    {i < 2 ? activeGame.toUpperCase() : "OFFLINE"}
                                </div>
                            </div>)}
                    </div>
                </div>

                <div className="tournament-preview">
                    <img src="https://cdn.escharts.com/uploads/public/62e/ce2/f51/62ece2f5147ee012365892.png" alt="Tournament" />
                    <div className="tournament-info">
                        <h4>VALORANT CHAMPIONS</h4>
                        <div className="tournament-meta">
                            <span className="prize-pool">$1M PRIZE POOL</span>
                            <span className="starts-in">STARTS IN 3D</span>
                        </div>
                        <button className="register-btn">REGISTER NOW</button>
                    </div>
                </div>

                <div className="featured-rewards">
                    <h3 className="panel-title">HOT REWARDS</h3>
                    <div className="reward-carousel">
                        <div className="reward-item">
                            <img src="https://media.direct.playstation.com/is/image/sierialto/dualsense-ps5-controller-white-accessory-front" alt="PS5 Controller" />
                            <div className="reward-info">
                                <span className="reward-name">PS5 Controller</span>
                                <span className="reward-cost">5,000 💎</span>
                            </div>
                        </div>
                        <div className="reward-item">
                            <img src="https://resource.logitech.com/w_692,c_lpad,ar_4:3,q_auto,f_auto,dpr_1.0/d_transparent.gif/content/dam/logitech/en/products/gaming-mice/g-pro-x-superlight-2/gallery/g-pro-x-superlight-2-gallery-1-black.png" alt="Gaming Mouse" />
                            <div className="reward-info">
                                <span className="reward-name">Logitech G Pro</span>
                                <span className="reward-cost">3,500 💎</span>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
};

export default VisualFirstDashboard;